import {useEffect} from 'react'
import {useLocation} from 'react-router'
import {track} from '@core/utils/analytics'

/**
 * useBehavioralTracking — passive behavioral instrumentation for GA4.
 *
 * Fires: scroll_depth, engagement_pulse, page_exit_intent. Called once from root App(). No return
 * value.
 *
 * connection_lost/connection_restored were removed 2026-08-01 (plan
 * 2026_08_01_ga4_wiring_defect_remediation.md, C1) — they fired off the browser's own
 * offline/online events with no user action behind them, which `@core/utils/CLAUDE.md`'s
 * collection policy does not allow (a browser was present, but nobody did anything).
 */
export function useBehavioralTracking(): void {
    const location = useLocation()
    const pathname = location.pathname

    // --- Scroll depth ---
    useEffect(() => {
        const thresholds = [25, 50, 75, 100] as const
        const fired = new Set<number>()
        let rafPending = false

        function onScroll() {
            if (rafPending) return
            rafPending = true
            requestAnimationFrame(() => {
                rafPending = false
                const scrollHeight = document.documentElement.scrollHeight
                if (scrollHeight === 0) return
                const depth = Math.floor(
                    ((window.scrollY + window.innerHeight) / scrollHeight) * 100,
                )
                for (const threshold of thresholds) {
                    if (depth >= threshold && !fired.has(threshold)) {
                        fired.add(threshold)
                        track('scroll_depth', {depth: threshold, page: pathname})
                    }
                }
            })
        }

        window.addEventListener('scroll', onScroll, {passive: true})
        return () => window.removeEventListener('scroll', onScroll)
    }, [pathname])

    // --- Engagement pulse ---
    useEffect(() => {
        let visibleSeconds = 0
        let lastTick = document.visibilityState === 'visible' ? Date.now() : null

        function onVisibilityChange() {
            if (document.visibilityState === 'visible') {
                lastTick = Date.now()
            } else {
                if (lastTick !== null) {
                    visibleSeconds += (Date.now() - lastTick) / 1000
                    lastTick = null
                }
            }
        }

        document.addEventListener('visibilitychange', onVisibilityChange)

        // A hidden tab emits nothing. Until 2026-08-01 the pulse fired on every tick regardless of
        // visibility — only the `visibleSeconds` accumulator was gated — so a tab parked in the
        // background sent an event every 30s with a frozen count. GA4 ends a session after 30
        // minutes without an event, so those pulses also kept dead sessions alive indefinitely,
        // which distorts session counts and duration on both properties.
        const interval = setInterval(() => {
            if (lastTick === null) return
            visibleSeconds += (Date.now() - lastTick) / 1000
            lastTick = Date.now()
            track('engagement_pulse', {
                visible_seconds: Math.round(visibleSeconds),
                page: pathname,
            })
        }, 30_000)

        return () => {
            document.removeEventListener('visibilitychange', onVisibilityChange)
            clearInterval(interval)
        }
    }, [pathname])

    // --- Exit intent ---
    useEffect(() => {
        const pageEnteredAt = Date.now()

        function onBeforeUnload() {
            track('page_exit_intent', {
                page: pathname,
                time_on_page_ms: Date.now() - pageEnteredAt,
            })
        }

        window.addEventListener('beforeunload', onBeforeUnload)
        return () => window.removeEventListener('beforeunload', onBeforeUnload)
    }, [pathname])
}
